import { audioService } from '@/services/audioService';
import { authService } from '@/services/authService';
import { useAuthStore } from '@/state/authStore';
import { userProfileService } from '@/services/userProfileService';
import { clearRoot, el, mount } from './dom';
import { showMenu } from './MenuOverlay';

export function showSettings(): void {
  clearRoot();

  const auth = useAuthStore.getState();
  let muted = audioService.isMuted();

  const container = el(
    'div',
    { class: 'fixed inset-0 flex flex-col items-stretch px-4 pb-6 fade-in safe-top' },
    []
  );

  const header = el('div', { class: 'flex items-center justify-between mb-6 mt-2' }, [
    backButton(() => showMenu()),
    el('h2', { class: 'font-display text-3xl text-white' }, ['⚙ Ajustes'])
  ]);
  container.append(header);

  const nicknameInput = el('input', {
    type: 'text',
    placeholder: 'Tu nickname',
    maxlength: 20,
    value: auth.displayName ?? '',
    class:
      'w-full rounded-xl bg-panel border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-accent'
  }) as HTMLInputElement;

  const nicknameStatus = el('div', { class: 'text-white/40 text-xs h-4' }, ['']);
  let saveTimer: number | null = null;
  nicknameInput.addEventListener('input', () => {
    const value = nicknameInput.value.trim();
    useAuthStore.getState().setNickname(value);
    if (saveTimer) window.clearTimeout(saveTimer);
    nicknameStatus.innerHTML = 'Guardando...';
    saveTimer = window.setTimeout(async () => {
      if (!useAuthStore.getState().uid) {
        nicknameStatus.innerHTML = '✓ Guardado local';
        return;
      }
      const ok = await userProfileService.setNickname(
        useAuthStore.getState().uid!,
        value,
        useAuthStore.getState().isAnonymous
      );
      if (!value) {
        nicknameStatus.innerHTML = '';
      } else if (ok) {
        nicknameStatus.innerHTML = '✓ Guardado en la nube';
      } else {
        nicknameStatus.innerHTML = '⚠ Solo guardado local (revisa reglas de Firestore)';
      }
    }, 600);
  });

  const muteBtn = el('button', { class: 'btn-secondary w-full' }, [
    muted ? '🔇 Sonido apagado' : '🔊 Sonido activo'
  ]);
  muteBtn.addEventListener('click', () => {
    muted = audioService.toggleMute();
    muteBtn.innerHTML = muted ? '🔇 Sonido apagado' : '🔊 Sonido activo';
  });

  const linkLabel = auth.isAnonymous ? '🔗 Vincular cuenta Google' : '✓ Cuenta Google vinculada';
  const linkBtn = el(
    'button',
    { class: auth.isAnonymous ? 'btn-secondary w-full' : 'btn-ghost w-full opacity-60' },
    [linkLabel]
  );
  const linkStatus = el('div', { class: 'text-white/40 text-xs h-4 text-center' }, ['']);

  if (auth.isAnonymous) {
    linkBtn.addEventListener('click', async () => {
      linkBtn.innerHTML = '...';
      linkStatus.innerHTML = '';
      const u = await authService.upgradeToGoogle();
      if (!u) {
        linkBtn.innerHTML = linkLabel;
        linkStatus.innerHTML = 'No se pudo vincular. Reintenta.';
        return;
      }
      const previousUid = useAuthStore.getState().uid;
      const previousNick =
        nicknameInput.value.trim() || u.displayName || useAuthStore.getState().displayName || '';

      try {
        await userProfileService.migrateLocalToGoogle(previousUid, u.uid, previousNick);
      } catch (err) {
        console.error('migrate failed', err);
      }

      useAuthStore.getState().setUser({
        uid: u.uid,
        displayName: previousNick || u.displayName,
        isAnonymous: false
      });

      const profile = await userProfileService.load(u.uid);
      if (profile) {
        useAuthStore.getState().setProfile({
          nickname: profile.nickname,
          bestScore: profile.bestScore,
          bestLevel: profile.bestLevel,
          totalGames: profile.totalGames
        });
      }
      showSettings();
    });
  }

  container.append(
    el('div', { class: 'flex flex-col gap-4' }, [
      el('label', { class: 'flex flex-col gap-2' }, [
        el('span', { class: 'text-white/70 text-sm uppercase tracking-wider' }, ['Nickname']),
        nicknameInput,
        nicknameStatus
      ]),
      muteBtn,
      linkBtn,
      linkStatus
    ])
  );

  if (auth.bestScore > 0) {
    container.append(
      el('div', { class: 'mt-6 panel p-4 text-center' }, [
        el('div', { class: 'text-white/50 text-[11px] tracking-[0.25em] uppercase mb-1' }, [
          'Tu mejor récord'
        ]),
        el('div', { class: 'font-display text-3xl text-accent' }, [
          auth.bestScore.toLocaleString('es-MX')
        ]),
        el('div', { class: 'text-white/50 text-xs' }, [
          `Nivel ${auth.bestLevel + 1} · ${auth.totalGames} ${auth.totalGames === 1 ? 'partida' : 'partidas'}`
        ])
      ])
    );
  }

  container.append(
    el('div', { class: 'absolute bottom-6 left-0 right-0 text-center text-white/30 text-xs' }, [
      'Math Ninja · v0.1'
    ])
  );

  mount(container);
}

function backButton(onClick: () => void): HTMLElement {
  const b = el('button', { class: 'btn-ghost px-4 py-2 text-sm' }, ['← Volver']);
  b.addEventListener('click', onClick);
  return b;
}
